import React from 'react';
import { getContext } from 'common';
import _merge from 'lodash/merge';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

import styled from './styles';

const mapTypographyToElement = (typographyVariant) => {
  switch (typographyVariant) {
    case 'HEADLINE_ALPHA':
      return 'h2';
    case 'HEADLINE_NUMERIC':
      return 'h2';
    case 'TITLE_24':
      return 'h2';
    case 'TITLE_16':
      return 'h2';
    case 'BODY':
      return 'span';
    case 'CAPTION':
      return 'span';
    case 'MICRO':
      return 'span';
    default:
      return 'span';
  }
};

export interface TypographyProps {
  /**
   * styled-components "as" prop
   * https://styled-components.com/docs/api#as-polymorphic-prop
   * */
  as?: React.ComponentType<any> | undefined;
  /** The variant of the typography, this specifies the font-family, font-size, line-height and the default DOM element tag. */
  variant?: 'HEADLINE_ALPHA' | 'HEADLINE_NUMERIC' | 'TITLE_24' | 'TITLE_16' | 'BODY' | 'LABEL' | 'CAPTION' | 'MICRO';
  /** The maximum number of lines before cutting the text. */
  maxLines?: number;
  /** To override the style of component. */
  style?: React.CSSProperties;
  /** The content of :before pseudo selector. */
  beforeContent?: string;
  /** The content of :after pseudo selector. */
  afterContent?: string;
  /** The content. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** To pass all other props to an unknown element. */
  [x:string]: any;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>((props: TypographyProps, ref) => getContext(({ RTL, theme }) => {
  const {
    variant,
    as,
    style,
    afterContent,
    beforeContent,
    children,
    maxLines,
    theme: themeProp,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme.TYPOGRAPHY, themeProp);
  const Component = as || mapTypographyToElement(variant);

  return (
    <styled.Content
      ref={ref}
      RTL={RTL}
      afterContent={afterContent}
      beforeContent={beforeContent}
      maxLines={maxLines}
      lineHeight={style.lineHeight || Tokens.TYPOGRAPHY[variant].lineHeight}
      as={Component}
      style={{
        color: resolvedTheme.content,
        ...Tokens.TYPOGRAPHY[variant],
        ...style
      }}
      {...otherProps}
    >
      {children}
    </styled.Content>);
}));

Typography.displayName = 'Typography';

Typography.defaultProps = {
  variant: 'BODY',
  maxLines: undefined,
  as: undefined,
  style: {},
  beforeContent: undefined,
  afterContent: undefined,
  theme: {}
};

export default Typography;
